import React, { useState, useEffect } from 'react';
import { string } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { currentLocale } from '../../util/locale';
import { listingCustomerId } from '../../util/clientData';
import { QRCodeSVG } from 'qrcode.react';
import css from './UserSendInvitationPage.module.css';
import { contactUsMessage, mailchimp, mailchimpSendInvitation, saveNotification } from '../../util/api';
import { contactUsMessageOldUser, checkInvite } from '../../util/api';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withViewport } from '../../util/uiHelpers';
import UserSendInvitationForm from './UserSendInvitationForm';
import { types as sdkTypes } from '../../util/sdkLoader';
import { requestCreateListingDraft, requestCreateQrInvitationDraft } from './UserSendInvitation.duck';
import moment from 'moment';

// How often the open QR code checks whether the client has signed up yet.
const CLAIM_POLL_INTERVAL_MS = 4000;

// Option B next to the email form below: a one-time QR code for a client
// standing in front of the photographer right now. Generating a code creates
// one blank draft listing under the photographer's account (nothing is created
// just by opening this tab); the code carries that listing's id, and the
// client claims exactly that listing when they sign up on their own device -
// see RequestShootSection in ProfilePage.js. One code = one client, so the
// photographer generates a new one for the next person. While a code is shown
// it watches for the client finishing signup, so the photographer sees it
// without having to reload anything.
const QrInviteOption = ({ photoGrapherId, onCreateInvitation }) => {
  const [copied, setCopied] = useState(false);
  const [listingId, setListingId] = useState(null);
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(false);
  const [claimed, setClaimed] = useState(false);

  // checkInvite only reports "valid" while the invitation is unused, so it
  // turning invalid for a code we just created means the client claimed it.
  useEffect(() => {
    if (!listingId || claimed) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      checkInvite({ listingId, photographerId: photoGrapherId })
        .then(res => {
          if (res && res.valid === false) {
            setClaimed(true);
          }
        })
        .catch(() => {});
    }, CLAIM_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [listingId, claimed, photoGrapherId]);

  if (!photoGrapherId || typeof window === 'undefined') {
    return null;
  }

  const inviteUrl = listingId
    ? `${window.location.origin}/u/${photoGrapherId}?listing=${listingId}`
    : null;

  const handleGenerate = () => {
    setInProgress(true);
    setError(false);
    setCopied(false);
    setClaimed(false);
    onCreateInvitation()
      .then(newListingId => {
        if (!newListingId) {
          throw new Error('No listingId returned');
        }
        setListingId(newListingId);
      })
      .catch(() => setError(true))
      .finally(() => setInProgress(false));
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(inviteUrl)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const buttonStyle = {
    padding: '8px 14px',
    border: 'none',
    borderRadius: 6,
    background: '#FF6713',
    color: '#fff',
    fontWeight: 600,
    cursor: inProgress ? 'default' : 'pointer',
    opacity: inProgress ? 0.6 : 1,
  };

  return (
    <div style={{ textAlign: 'center', padding: '24px 16px' }}>
      <p style={{ marginBottom: 16, color: '#4B5563' }}>
        <FormattedMessage id="UserSendInvitation.qrHint" />
      </p>
      {inviteUrl && claimed ? (
        <>
          <p style={{ marginBottom: 16, fontWeight: 600, color: '#15803D' }}>
            <FormattedMessage id="UserSendInvitation.clientSignedUp" />
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => window.location.assign('/listings')}
              style={buttonStyle}
            >
              <FormattedMessage id="UserSendInvitation.showListings" />
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={inProgress}
              style={{
                ...buttonStyle,
                background: 'transparent',
                color: '#FF6713',
                border: '1px solid #FF6713',
              }}
            >
              <FormattedMessage id="UserSendInvitation.newCode" />
            </button>
          </div>
        </>
      ) : inviteUrl ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <QRCodeSVG value={inviteUrl} size={160} />
          </div>
          <div style={{ display: 'flex', gap: 8, maxWidth: 360, margin: '0 auto 16px' }}>
            <input
              type="text"
              readOnly
              value={inviteUrl}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 6 }}
            />
            <button type="button" onClick={handleCopy} style={buttonStyle}>
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={inProgress}
            style={{
              ...buttonStyle,
              background: 'transparent',
              color: '#FF6713',
              border: '1px solid #FF6713',
            }}
          >
            <FormattedMessage id="UserSendInvitation.newCode" />
          </button>
        </>
      ) : (
        <button type="button" onClick={handleGenerate} disabled={inProgress} style={buttonStyle}>
          {inProgress ? 'Creating...' : 'Generate QR code'}
        </button>
      )}
      {error ? (
        <p style={{ marginTop: 12, color: '#B91C1C' }}><FormattedMessage id="UserSendInvitation.error" /></p>
      ) : null}
    </div>
  );
};

const InviteModeTabs = ({ mode, setMode }) => {
  const tabStyle = active => ({
    flex: 1,
    padding: '10px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    fontWeight: 600,
    borderBottom: active ? '2px solid #FF6713' : '2px solid #E5E7EB',
    color: active ? '#FF6713' : '#6B7280',
  });

  return (
    <div style={{ display: 'flex', marginBottom: 8 }}>
      <div style={tabStyle(mode === 'email')} onClick={() => setMode('email')}>
        <FormattedMessage id="UserSendInvitation.optionEmail" />
      </div>
      <div style={tabStyle(mode === 'qr')} onClick={() => setMode('qr')}>
        <FormattedMessage id="UserSendInvitation.optionQr" />
      </div>
    </div>
  );
};

const UserSendInvitationPage = props => {
  const {
    intl,
    scrollingDisabled,
    currentUser,
    userShowError,
    userId,
    setUserSendInvitationMobile,
    onCreateListingDraft,
    onCreateQrInvitation,
  } = props;

  const photoGrapherId = currentUser && currentUser.id?.uuid;
  const photoGrapherName = currentUser?.attributes?.profile?.displayName;
  const photoGrapherImageUrl =
    currentUser?.profileImage?.attributes?.variants?.['square-small2x'].url;
  const dateAndTime = moment().format('MMMM Do YYYY, h:mm:ss a');

  const [successMsg, setSuccessMsg] = useState(false);
  const [inviteMode, setInviteMode] = useState('email');

  const handleSubmit = values => {
    const { firstName, lastName, email, message, description, language } = values;
    const params = { ...values, email: email?.toLowerCase(), dateAndTime, photoGrapherId };

    setSuccessMsg(true);
    setTimeout(() => {
      setSuccessMsg(false);
      setUserSendInvitationMobile(false);
    }, 2000);

    onCreateListingDraft(params)
      .then(async res => {
        const { listingData, userAlreadyExist } = res || {};
        const listingId = listingData?.data?.data?.id?.uuid;
        const customerId = listingCustomerId(listingData?.data?.data);

        if (!userAlreadyExist) {
          if (listingId) {
            await contactUsMessage({
              language,
              firstName,
              lastName,
              email: email?.toLowerCase(),
              message,
              photoGrapherId,
              photoGrapherName,
              photoGrapherImageUrl,
              listingId,
            });
          }
          // await mailchimp({ ...values }).catch(err => console.log(err));
        } else {
          if (listingId) {
            await contactUsMessageOldUser({
              language,
              firstName,
              lastName,
              email: email?.toLowerCase(),
              message,
              photoGrapherId,
              photoGrapherName,
              photoGrapherImageUrl,
              listingId,
            });
          }
        }

        // Save notification to customer's profile
        if (customerId && listingId) {
          await saveNotification({
            userId: customerId,
            notification: {
              id: `${listingId}-${Date.now()}`,
              listingId,
              createdAt: new Date().toISOString(),
            },
          }).catch(e => console.error('Failed to save notification:', e));
        }

        return res;
      })
      .catch(err => {
        console.log('Something went wrong');
        console.error(err);
      });
  };

  return (
    <div className={css.contactWrapper}>
      {successMsg && (
        <div className={css.successToast}>
          <FormattedMessage id="UserSendInvitation.sentSuccess" />
        </div>
      )}
      <InviteModeTabs mode={inviteMode} setMode={setInviteMode} />
      {inviteMode === 'email' ? (
        <UserSendInvitationForm
          className={css.form}
          onSubmit={handleSubmit}
          initialValues={{ language: currentLocale() }}
          isSendMail={successMsg}
        />
      ) : (
        <QrInviteOption
          photoGrapherId={photoGrapherId}
          onCreateInvitation={onCreateQrInvitation}
        />
      )}
    </div>
  );
};

UserSendInvitationPage.defaultProps = {
  rootClassName: null,
  className: null,
};

UserSendInvitationPage.propTypes = {
  rootClassName: string,
  className: string,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onCreateListingDraft: (values, config) => dispatch(requestCreateListingDraft(values, config)),
  onCreateQrInvitation: () => dispatch(requestCreateQrInvitationDraft()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const UserSendInvitation = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport,
  injectIntl
)(UserSendInvitationPage);

export default UserSendInvitation;
